import SelectInput, {
  OptionType,
  SelectInputProps,
} from "@/components/select-input/select-input";
import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  FieldOptionsExtractProps,
  FieldOptionsMappingProps,
  SelectInputOptionsMappingProps,
  useFieldOptionsExtract,
  useFieldOptionsMapping,
  useFormSelectInputCommonClassNames,
} from "./common";
import { FormItemCommonProps, FormItemContent } from "../common";
import { FormField } from "@/components/ui/form";
import { callAll, cn } from "@/lib/utils";

export type FormSelectInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  name: TName;
  inline?: boolean;
  isActive?: boolean;
  displaySelected?: boolean;
  isNullable?: boolean;
} & FormItemCommonProps &
  Omit<
    SelectInputOptionsMappingProps<TFieldValues, TName, Value, Option, IsMulti>,
    "isMulti"
  > &
  Omit<SelectInputProps<Value, Option, IsMulti, Group>, "form">;

export type FormSelectInputItemCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
  Value = string | number,
  Option extends OptionType<Value> = OptionType<Value>,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<
  FormSelectInputProps<
    TFieldValues,
    TName,
    TContext,
    TTransformedValues,
    Value,
    Option,
    IsMulti,
    Group
  >,
  "form" | "name" | "label" | "mapping"
>;

export function FormSelectInputItem<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
  Value,
  Option extends OptionType<Value>,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>({
  form,
  name,
  label,
  container,
  assign,
  mapping,
  extract,
  onChange,
  onBlur,
  className,
  classNames: _classNames,
  inline,
  displaySelected,
  isActive,
  isNullable,
  empty,
  ...props
}: FormSelectInputProps<
  TFieldValues,
  TName,
  TContext,
  TTransformedValues,
  Value,
  Option,
  IsMulti,
  Group
>) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const value = useFieldOptionsMapping({
          ...props,
          field,
          assign,
          mapping,
          extract,
          isNullable,
          empty,
          // cache
        } as FieldOptionsMappingProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const ext = useFieldOptionsExtract({
          ...props,
          field,
          assign,
          mapping,
          extract,
          isNullable,
          empty,
          // updateCache
        } as FieldOptionsExtractProps<
          TFieldValues,
          TName,
          Value,
          Option,
          IsMulti
        >);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const classNames = useFormSelectInputCommonClassNames({
          ...props,
          classNames: _classNames,
          isActive,
          inline,
        });

        return (
          <FormItemContent
            {...container}
            label={label}
            inline={container?.inline ?? inline}
          >
            <SelectInput
              className={cn("w-full group/select", className)}
              classNames={classNames}
              {...(field as any)}
              value={value || ""}
              onChange={(v, meta) => {
                const value = ext(v as any);
                const ret = field.onChange(value);
                if (onChange) onChange(v as any, meta as any);
                return ret;
              }}
              onBlur={callAll(field.onBlur, onBlur)}
              {...props}
              placeholder={
                inline
                  ? label
                    ? `${label}${props?.isMulti && ((value as any[])?.length ?? 0) > 0 ? ` (${(value as any[])?.length})` : ""}`
                    : undefined
                  : props.placeholder
              }
              controlShouldRenderValue={
                displaySelected
                  ? true
                  : inline
                    ? false
                    : props.controlShouldRenderValue
              }
            />
          </FormItemContent>
        );
      }}
    />
  );
}
